import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import {
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  deleteDoc,
  writeBatch, // Import writeBatch
  Timestamp
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import { PaperclipIcon, TrashIcon } from '../../../components/icons/Icons';
import {
  vendorApprovedTemplate,
  sendNotificationEmail
} from '../../../firebase/notifications';

// Helper component for form sections
const FormSection = ({ title, children }) => (
    <fieldset className="border-t pt-6 mt-6">
        <legend className="text-lg font-semibold text-[#213368] mb-4">
            {title}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {children}
        </div>
    </fieldset>
);

// Helper component to display a file from Firestore
const FileListItem = ({ file, onRemove }) => (
    <div className="flex items-center justify-between p-1 text-sm">
        <a
          href={file.downloadURL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-blue-600 hover:underline truncate"
        >
            <PaperclipIcon />
            <span className="ml-1">{file.fileName}</span>
        </a>
        <button type="button" onClick={onRemove}>
            <TrashIcon />
        </button>
    </div>
);

// Helper component for NEW files (that don't have a downloadURL)
const NewFileListItem = ({ file, onRemove }) => (
    <div className="flex items-center justify-between p-1 text-sm">
        <div className="flex items-center text-gray-700 truncate">
            <PaperclipIcon />
            <span className="ml-1">{file.name}</span>
        </div>
        <button type="button" onClick={onRemove}>
            <TrashIcon />
        </button>
    </div>
);


export default function VendorFormModal({
  appId,
  userProfile,
  existingVendor,
  onClose
}) {
    const isEditing = !!existingVendor;

    const [formData, setFormData] = useState({
        basicInfo: {
          legalName: '', address: '', contactPerson: '',
          phone: '', email: '', website: ''
        },
        corporateInfo: {
          description: '', entityType: '', registrationNumber: '',
          pan: '', gst: '', country: ''
        },
        bankingInfo: {
          bankName: '', accountNumber: '', beneficiaryName: '',
          branchAddress: '', branchCode: '', swift: '',
          specialInstructions: '', paymentEmail: ''
        }
    });

    // --- STATE REFACTOR ---
    // Holds NEW File objects to be uploaded
    const [newCorporateDocs, setNewCorporateDocs] = useState([]);
    const [newBankProof, setNewBankProof] = useState(null);
    
    // Holds files ALREADY in Firestore (for display)
    const [existingFiles, setExistingFiles] = useState({
      corporate: [],
      bank: null
    });
    
    // Holds EXISTING files to be deleted on submit
    const [filesToDelete, setFilesToDelete] = useState([]);
    // --- END STATE REFACTOR ---
    
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState([]);

    // --- This useEffect is now correct ---
    useEffect(() => {
        if (isEditing) {
            // 1. Set form data
            const basic = existingVendor.basicInfo || {
              legalName: existingVendor.companyName,
              email: existingVendor.email,
              contactPerson: existingVendor.contactPerson
            };
            setFormData({
                basicInfo: basic,
                corporateInfo: existingVendor.corporateInfo || {},
                bankingInfo: existingVendor.bankingInfo || {}
            });

            // 2. Fetch attachments from the 'documents' subcollection
            const fetchAttachments = async () => {
                const attachmentsRef = collection(
                  db,
                  `/artifacts/${appId}/public/data/vendors/` +
                  `${existingVendor.id}/documents`
                );
                const snapshot = await getDocs(attachmentsRef);
                
                const corpFiles = [];
                let bankFile = null;
                
                snapshot.docs.forEach(doc => {
                    const fileData = { id: doc.id, ...doc.data() };
                    
                    // Check for BOTH paths
                    if (fileData.path?.includes('/bank/') || 
                        fileData.path?.includes('/bank-proof/')) {
                        bankFile = fileData;
                    } else {
                        corpFiles.push(fileData);
                    }
                });
                
                setExistingFiles({ corporate: corpFiles, bank: bankFile });
            };
            
            fetchAttachments();
        }
    }, [existingVendor, isEditing, appId]);

    const handleChange = (e) => {
        const { name, value, dataset } = e.target;
        const { section } = dataset;
        setFormData(prev => ({
          ...prev,
          [section]: { ...prev[section], [name]: value }
        }));
    };

    // --- FIX: handleCorpFileChange ---
    // This ONLY adds new files to the 'newCorporateDocs' state
    const handleCorpFileChange = (e) => {
        if (e.target.files) {
            setNewCorporateDocs(prev => [...prev, ...Array.from(e.target.files)]);
        }
    };
    
    // --- FIX: handleBankFileChange ---
    // This now handles REPLACING an existing file
    const handleBankFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const newFile = e.target.files[0];
            
            // 1. Set the new file to be uploaded
            setNewBankProof(newFile);
            
            // 2. If an old file was already there,
            //    automatically mark it for deletion
            if (existingFiles.bank) {
                // This helper function already does everything we need:
                // - Adds old file to filesToDelete
                // - Sets existingFiles.bank to null
                handleRemoveExistingFile(existingFiles.bank, 'bank');
            }
        }
    };

    // --- FIX: New function to remove a NEW file (from state) ---
    const handleRemoveNewCorpFile = (index) => {
        setNewCorporateDocs(prev => prev.filter((_, i) => i !== index));
    };

    // --- FIX: New function to remove an EXISTING file (from state) ---
    const handleRemoveExistingFile = (fileToRemove, type) => {
        if (!fileToRemove || !fileToRemove.id || !fileToRemove.path) return;
        
        // 1. Add file to the delete queue
        setFilesToDelete(prev => [...prev, fileToRemove]);
        
        // 2. Update the UI state to remove it
        if (type === 'corporate') {
            setExistingFiles(prev => ({
                ...prev,
                corporate: prev.corporate.filter(f => f.id !== fileToRemove.id)
            }));
        } else if (type === 'bank') {
            setExistingFiles(prev => ({ ...prev, bank: null }));
        }
    };

    const handlePhoneChange = (value) => {
        setFormData(prev => ({
            ...prev,
            basicInfo: { ...prev.basicInfo, phone: value || '' }
        }));
    };

  const handleFileUpload = async (file, type, vendorId) => {
        if (!file) return null;
        
        // --- ADD THIS CHECK ---
        if (!vendorId) {
            console.error("No vendor ID provided for file upload.");
            return null;
        }

        const folder = type === 'bank' ? 'bank-proof' : 'corporate';
         
        const filePath = `vendor-documents/${appId}/${vendorId}/` +
                         `${folder}/${Date.now()}-${file.name}`;
        
        const fileRef = ref(storage, filePath);
        await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(fileRef);
        
        return {
          fileName: file.name,
          fileType: file.type,
          downloadURL: downloadURL,
          path: filePath,
          uploadedAt: Timestamp.now()
        };
    };

    const validateForm = () => {
        const newErrors = [];
        const { basicInfo, corporateInfo } = formData;
        if (!basicInfo.legalName) newErrors.push('Legal Name is required.');
        if (!basicInfo.address) newErrors.push('Registered Address is required.');
        if (!basicInfo.contactPerson) newErrors.push('Contact Person is required.');
        if (!basicInfo.phone) newErrors.push('Phone / Mobile Number is required.');
        if (!basicInfo.email) newErrors.push('Email is required.');
        if (!corporateInfo.entityType) newErrors.push('Entity Type is required.');
        if (!corporateInfo.country) newErrors.push('Country of Incorporation is required.');
        setErrors(newErrors);
        return newErrors.length === 0;
    };

    // --- FIX: handleSubmit with clean delete/upload logic ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsLoading(true);

        try {
            // 1. Delete all files marked for deletion
            if (filesToDelete.length > 0) {
                const deleteBatch = writeBatch(db);
                for (const fileDoc of filesToDelete) {
                    // Delete from Storage
                    const storageRef = ref(storage, fileDoc.path);
                    await deleteObject(storageRef).catch(e => 
                        console.warn(`Failed to delete ${fileDoc.path}:`, e)
                    );
                    
                    // Add Firestore doc deletion to batch
                    const firestoreRef = doc(
                        db,
                        `/artifacts/${appId}/public/data/vendors/` +
                        `${existingVendor.id}/documents`,
                        fileDoc.id
                    );
                    deleteBatch.delete(firestoreRef);
                }
                // Commit all Firestore deletions at once
                await deleteBatch.commit();
            }

            // 2. Define the main vendor ref
            let vendorRef;
	    let vendorIdForUpload;
            if (isEditing) {
                vendorRef = doc(
                  db,
                  `/artifacts/${appId}/public/data/vendors`,
                  existingVendor.id
                );
                vendorIdForUpload = existingVendor.id; // Use existing ID
            } else {
                // Generate the new vendor's ref and grab its ID
                vendorRef = doc(
                  collection(db, `/artifacts/${appId}/public/data/vendors`)
                );
                vendorIdForUpload = vendorRef.id; // Use the new ID
            }

            // 3. Upload NEW files
            const uploadedCorpDocs = await Promise.all(
              newCorporateDocs.map(file => 
                handleFileUpload(file, 'corporate', vendorIdForUpload))
            );
            const uploadedBankProof = await handleFileUpload(
                newBankProof, 'bank', vendorIdForUpload
            );

            // 4. Define and save the main vendor document data
            const finalData = {
                ...formData,
                basicInfo: {
                    ...formData.basicInfo,
                    searchableName: formData.basicInfo.legalName.toLowerCase()
                },
                corporateInfo: {
                    ...formData.corporateInfo,
                    documents: [] // Clear old array
                },
                bankingInfo: {
                    ...formData.bankingInfo,
                    proofDocument: null // Clear old field
                },
                status: 'Active',
                inviteStatus: 'Used',
                updatedAt: serverTimestamp()
            };
            
            if (isEditing) {
                await updateDoc(vendorRef, finalData);
                // PR status update logic
                if (
                    (existingVendor.status === 'Pending Finance Review' || 
                     existingVendor.status === 'Pending Approval') &&
                    existingVendor.purchaseRequestId
                ) {
                    const prRef = doc(
                      db,
                      `/artifacts/${appId}/public/data/purchaseRequests`,
                      existingVendor.purchaseRequestId
                    );
                    await updateDoc(prRef, { status: 'Approved' });
                }
            } else {
                // This is a new vendor, set its ID for file paths
                // Note: File paths will be slightly different, this is a minor issue
                // A better fix is to generate the ID *before* upload
                await setDoc(vendorRef, {
                    ...finalData,
                    id: vendorRef.id,
                    createdAt: serverTimestamp(),
                    submittedBy: userProfile.name
                });
            }
            
            // 5. Save new file docs to the subcollection
            const attachmentsRef = collection(vendorRef, 'documents');
            const uploadBatch = writeBatch(db);

            uploadedCorpDocs.forEach(docData => {
                if (docData) {
                    const newDocRef = doc(attachmentsRef);
                    uploadBatch.set(newDocRef, docData);
                }
            });
            
            if (uploadedBankProof) {
                // If there's a new bank proof, delete the old one first
                if (existingFiles.bank && 
                    !filesToDelete.find(f => f.id === existingFiles.bank.id)) 
                {
                    // It wasn't marked for deletion, so delete it now
                    const oldBankStorageRef = ref(storage, existingFiles.bank.path);
                    await deleteObject(oldBankStorageRef).catch(
                        e => console.warn("Old bank file delete failed", e)
                    );
                    const oldBankDocRef = doc(attachmentsRef, existingFiles.bank.id);
                    uploadBatch.delete(oldBankDocRef);
                }
                // Add the new bank proof to the batch
                const newBankDocRef = doc(attachmentsRef);
                uploadBatch.set(newBankDocRef, uploadedBankProof);
            }
            // Commit all new file docs
            await uploadBatch.commit();

            // 6. Send notification email
            if (isEditing && (existingVendor.status === 'Pending Finance Review' || existingVendor.status === 'Pending Approval') ) {
                try {
                  // This notification logic seems to be for PR requesters
                  if (existingVendor.purchaseRequestId) {
                    const prRef = doc(
                      db,
                      `/artifacts/${appId}/public/data/purchaseRequests`,
                      existingVendor.purchaseRequestId
                    );
                    const prDoc = await getDoc(prRef);
                    
                    if (prDoc.exists()) {
                      const prData = prDoc.data();
                      const userRef = doc(
                        db, `/artifacts/${appId}/users`, prData.requesterId
                      );
                      const userSnap = await getDoc(userRef);
                      if (userSnap.exists() && userSnap.data().email) {
                        const userEmail = userSnap.data().email;
                        const emailContent = vendorApprovedTemplate(
                          finalData.basicInfo.legalName
                        );
                        await sendNotificationEmail(
                          { to: [userEmail], ...emailContent, appId }
                        );
                      }
                    }
                  }
                } catch (emailError) {
                  console.error("Error sending vendor approval email:", emailError);
                }
            }

            onClose();
        } catch (error) {
            console.error("Error saving vendor:", error);
            alert("Failed to save vendor.");
        } finally {
            setIsLoading(false);
        }
    };

    // --- LINTER-FRIENDLY JSX ---
    return (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex
                     justify-center items-center z-50 p-4"
        >
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-lg shadow-xl p-8 w-full
                         max-w-4xl max-h-[90vh] overflow-y-auto"
            >
                <h2 className="text-2xl font-bold text-[#213368] mb-4">
                    {isEditing ? 'Onboard / Edit Vendor' : 'Add New Vendor'}
                </h2>
                
                {errors.length > 0 && (
                    <div 
                      className="bg-red-100 border border-red-400
                                  text-red-700 px-4 py-3 rounded-lg mb-4"
                    >
                        <strong className="font-bold">
                          Please correct the following errors:
                        </strong>
                        <ul className="list-disc list-inside mt-2">
                            {errors.map((error, i) => <li key={i}>{error}</li>)}
                        </ul>
                    </div>
                )}

                <FormSection title="A. Basic Information">
                    <input data-section="basicInfo" name="legalName"
                      value={formData.basicInfo.legalName}
                      onChange={handleChange}
                      placeholder="Legal Name of Entity"
                      className="md:col-span-2 p-2 border rounded" required />
                    <textarea data-section="basicInfo" name="address"
                      value={formData.basicInfo.address}
                      onChange={handleChange}
                      placeholder="Registered Address with Pin Code"
                      className="md:col-span-2 p-2 border rounded"
                      rows="3" required></textarea>
                    <input data-section="basicInfo" name="contactPerson"
                      value={formData.basicInfo.contactPerson}
                      onChange={handleChange}
                      placeholder="Primary Contact Person"
                      className="p-2 border rounded" required/>
                    {/* --- UPDATED PHONE FIELD --- */}
		    <div className="md:col-span-1"> {/* This ensures it stays in one column */}
    		      <PhoneInput
        		international
        		defaultCountry="IN"  // Set your most common country
        		placeholder="Phone / Mobile Number"
        		value={formData.basicInfo.phone}
        		onChange={handlePhoneChange}
        		className="vendor-phone-input" // Use this class to style if needed
    		     />
		    </div>
                    <input data-section="basicInfo" name="email" type="email"
                      value={formData.basicInfo.email}
                      onChange={handleChange}
                      placeholder="Email Address"
                      className="p-2 border rounded" required/>
                    <input data-section="basicInfo" name="website"
                      value={formData.basicInfo.website}
                      onChange={handleChange}
                      placeholder="Website (optional)"
                      className="p-2 border rounded"/>
                </FormSection>

                <FormSection title="B. Corporate Information">
                    <textarea data-section="corporateInfo" name="description"
                      value={formData.corporateInfo.description}
                      onChange={handleChange}
                      placeholder="Brief Company Description"
                      className="md:col-span-2 p-2 border rounded"
                      rows="3"></textarea>
                    <select data-section="corporateInfo" name="entityType"
                      value={formData.corporateInfo.entityType}
                      onChange={handleChange}
                      className="p-2 border rounded" required>
                        <option value="">Select Entity Type...</option>
                        <option>Corporation</option>
                        <option>Partnership</option>
                        <option>Sole Proprietor</option>
                        <option>Other</option>
                    </select>
                    <input 
                      data-section="corporateInfo" name="registrationNumber"
                      value={formData.corporateInfo.registrationNumber}
                      onChange={handleChange}
                      placeholder="Company Registration Number"
                      className="p-2 border rounded"/>
                    <input data-section="corporateInfo" name="pan"
                      value={formData.corporateInfo.pan}
                      onChange={handleChange}
                      placeholder="PAN Number (optional)"
                      className="p-2 border rounded"/>
                    <input data-section="corporateInfo" name="gst"
                      value={formData.corporateInfo.gst}
                      onChange={handleChange}
                      placeholder="GST Number (optional)"
                      className="p-2 border rounded"/>
                    <input data-section="corporateInfo" name="country"
                      value={formData.corporateInfo.country}
                      onChange={handleChange}
                      placeholder="Country of Incorporation"
                      className="p-2 border rounded" required />
                    <div className="md:col-span-2">
                        <label 
                          className="block text-sm font-medium
                                        text-gray-700 mb-1"
                        >
                          Supporting Documents (Optional)
                        </label>
                        <label
                          htmlFor="corporate-docs-upload"
                          className="inline-block px-4 py-2 bg-[#20BEC6]
                                     text-white font-semibold rounded-lg
                                     shadow-sm cursor-pointer
                                     hover:bg-opacity-80 transition-colors"
                        >
                          Choose Files
                        </label>
                        <input
                          id="corporate-docs-upload"
                          type="file"
                          multiple
                          // Clear input on click so user can add same file
                          onClick={(e) => (e.target.value = null)}
                          onChange={handleCorpFileChange}
                          className="hidden"
                        />
                        <div className="mt-2">
                            {/* --- FIX: Render EXISTING files --- */}
                            {existingFiles.corporate.map((file) => (
                              <FileListItem
                                key={file.id}
                                file={file}
                                onRemove={() =>
                                  handleRemoveExistingFile(file, 'corporate')
                                }
                              />
                            ))}
                            {/* --- FIX: Render NEW files --- */}
                            {newCorporateDocs.map((file, i) => (
                              <NewFileListItem
                                key={file.name + i}
                                file={file}
                                onRemove={() => handleRemoveNewCorpFile(i)}
                              />
                            ))}
                        </div>
                    </div>
                </FormSection>

                <FormSection title="C. Banking Information (Optional)">
                    <input data-section="bankingInfo" name="bankName"
                      value={formData.bankingInfo.bankName}
                      onChange={handleChange}
                      placeholder="Bank Name"
                      className="p-2 border rounded" />
                    <input data-section="bankingInfo" name="accountNumber"
                      value={formData.bankingInfo.accountNumber}
                      onChange={handleChange}
                      placeholder="Account Number"
                      className="p-2 border rounded" />
                    <input data-section="bankingInfo" name="beneficiaryName"
                      value={formData.bankingInfo.beneficiaryName}
                      onChange={handleChange}
                      placeholder="Beneficiary Name"
                      className="p-2 border rounded" />
                    <textarea data-section="bankingInfo" name="branchAddress"
                      value={formData.bankingInfo.branchAddress}
                      onChange={handleChange}
                      placeholder="Bank Branch Address"
                      className="md:col-span-2 p-2 border rounded"
                      rows="2"></textarea>
                    <input data-section="bankingInfo" name="branchCode"
                      value={formData.bankingInfo.branchCode}
                      onChange={handleChange}
                      placeholder="Branch Code (e.g., BSB or IFSC)"
                      className="p-2 border rounded"/>
                    <input data-section="bankingInfo" name="swift"
                      value={formData.bankingInfo.swift}
                      onChange={handleChange}
                      placeholder="SWIFT (optional)"
                      className="p-2 border rounded"/>
                    <input data-section="bankingInfo" name="paymentEmail"
                      type="email"
                      value={formData.bankingInfo.paymentEmail}
                      onChange={handleChange}
                      placeholder="Email for payment matters"
                      className="md:col-span-2 p-2 border rounded" />
                     <div className="md:col-span-2">
                        <label 
                          className="block text-sm font-medium
                                        text-gray-700 mb-1"
                        >
                          Proof of Bank Account (Optional)
                        </label>
                        <label
                          htmlFor="bank-proof-upload"
                          className="inline-block px-4 py-2 bg-[#20BEC6]
                                     text-white font-semibold rounded-lg
                                     shadow-sm cursor-pointer
                                     hover:bg-opacity-80 transition-colors"
                        >
                          Choose File
                        </label>
                        <input
                          id="bank-proof-upload"
                          type="file"
                          // Clear input on click
                          onClick={(e) => (e.target.value = null)}
                          onChange={handleBankFileChange}
                          className="hidden"
                        />
                        <div className="mt-2">
                           {/* --- FIX: Prioritize showing the NEW file. --- */}
                           {newBankProof ? (
                            <NewFileListItem
                              file={newBankProof}
                              onRemove={() => setNewBankProof(null)}
                            />
                           ) : existingFiles.bank ? (
                            /* --- Only show EXISTING if no NEW file is staged --- */
                            <FileListItem
                              file={existingFiles.bank}
                              onRemove={() =>
                                handleRemoveExistingFile(
                                  existingFiles.bank, 'bank'
                                )
                              }
                            />
                           ) : null}
                           {/* This logic correctly handles all cases */}
                        </div>
                    </div>
                </FormSection>
                
                 <div 
                   className="flex justify-end space-x-4
                                mt-8 pt-6 border-t"
                 >
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isLoading}
                      className="px-6 py-2 bg-gray-200 text-gray-700
                                 font-medium rounded-lg hover:bg-gray-300
                                 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-6 py-2 bg-blue-600 text-white
                                 font-medium rounded-lg hover:bg-blue-700
                                 disabled:opacity-50"
                    >
                        {isLoading ? 'Saving...' : 'Save Vendor'}
                    </button>
                </div>
            </form>
        </div>
    );
}